import { useLayoutEffect, useRef, useState, useMemo, useCallback, useEffect } from 'react';

import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';

import { useContestSectionInfinite } from './useContestSectionInfinite';

export const useLazyLoadContest = ({
  rootMargin = '200px',
  threshold = 0.1,
}: {
  rootMargin?: string;
  threshold?: number;
} = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const options = {
      root: null,
      rootMargin,
      threshold,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry?.isIntersecting) {
        setIsVisible(true);
        setHasBeenVisible(true);
      } else {
        setIsVisible(false);
      }
    }, options);

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [rootMargin, threshold]);

  return {
    elementRef,
    isVisible,
    hasBeenVisible,
    shouldLoad: hasBeenVisible,
  };
};

export const useLazyLoadContestSectionInfinite = ({
  contestIds,
  sectionTitle,
  includeFull = false,
  rootMargin = '200px',
  threshold = 0.1,
  onRegisterSection,
}: {
  contestIds?: string[];
  sectionTitle: string;
  includeFull?: boolean;
  rootMargin?: string;
  threshold?: number;
  onRegisterSection?: (
    sectionTitle: string,
    refreshInfo: {
      queryKey: string[];
      isVisible: boolean;
      refetch: () => void;
      invalidateSection: () => void;
      clearSection: () => void;
    }
  ) => (() => void) | void;
}) => {
  const { elementRef, shouldLoad, hasBeenVisible, isVisible } = useLazyLoadContest({
    rootMargin,
    threshold,
  });

  // Use the new infinite query hook
  const {
    contests,
    isLoading,
    isInitialLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    loadedCount,
  } = useContestSectionInfinite({
    contestIds: contestIds || [],
    sectionTitle,
    includeFull,
    enabled: shouldLoad,
  });

  // Set up infinite scroll
  const { loaderRef, scrollContainerRef } = useInfiniteScroll({
    fetchMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    hasMore: hasNextPage,
    distance: 100,
  });

  const queryKey = useMemo(
    () => ['contest-section-infinite', sectionTitle, contestIds?.join(','), String(includeFull)],
    [sectionTitle, contestIds, includeFull]
  );

  const invalidateSection = useCallback(() => {
    refetch();
  }, [refetch]);

  const clearSection = useCallback(() => {
    // For infinite queries, we can't easily clear without refetching
    // This would need to be implemented differently if needed
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (onRegisterSection && hasBeenVisible) {
      const cleanup = onRegisterSection(sectionTitle, {
        queryKey,
        isVisible,
        refetch,
        invalidateSection,
        clearSection,
      });

      return cleanup;
    }
    return undefined;
  }, [
    onRegisterSection,
    hasBeenVisible,
    sectionTitle,
    queryKey,
    isVisible,
    refetch,
    invalidateSection,
    clearSection,
  ]);

  return {
    elementRef,
    scrollContainerRef,
    loaderRef,
    shouldLoad,
    hasBeenVisible,
    contests,
    isLoading: isLoading || isInitialLoading,
    isInitialLoading,
    isFetchingNextPage,
    error,
    refetch,
    invalidateSection,
    clearSection,
    totalContestIds: contestIds?.length || 0,
    loadedContestIds: loadedCount,
    hasNextPage,
    fetchNextPage,
    isVisible,
    queryKey,
  };
};
